/* eslint-disable @typescript-eslint/no-explicit-any */
/*!
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import {
  ERROR_CODE,
  RECAPTCHA_EXECUTE_TIMEOUT_MS,
  RECAPTCHA_MAX_RETRIES,
  RECAPTCHA_RETRY_DELAY_MS,
} from "../constant";
import { reCaptchaDelay } from "./utils";

export type CaptchaProvider = "grecaptcha" | "turnstile" | null;

export const getRecaptchaToken = async (
  action: string,
  _siteKey: string,
  executeRecaptcha: (action: string) => Promise<string>,
  timeoutMs = RECAPTCHA_EXECUTE_TIMEOUT_MS
): Promise<string> => {
  // grecaptcha.execute can hang (e.g. recaptcha/enterprise/reload blocked), reject so callers can retry.
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // eslint-disable-next-line @typescript-eslint/return-await
    return await Promise.race([
      executeRecaptcha(action),
      new Promise<string>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error("reCAPTCHA execute timed out.")),
          timeoutMs
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Runs `attempt` (fresh reCAPTCHA token + API call) and repeats it, up to `retries` times in total,
 * while the API rejects the token as invalid (1050, e.g. BROWSER_ERROR). Other errors and the last 1050 are re-thrown.
 */
export const retryOnRecaptchaInvalid = async <T>(
  attempt: () => Promise<T>,
  retries = RECAPTCHA_MAX_RETRIES,
  attemptNo = 1
): Promise<T> => {
  try {
    return await attempt();
  } catch (error) {
    if (
      (error as { code?: string })?.code !== ERROR_CODE.RECAPTCHA_INVALID ||
      attemptNo >= retries
    ) {
      throw error;
    }
    const delayTime = RECAPTCHA_RETRY_DELAY_MS * attemptNo; // linear backoff
    console.warn(
      `Request with reCAPTCHA failed. Retrying in ${delayTime / 1000}s... (${
        retries - attemptNo
      } retries left)`
    );
    await reCaptchaDelay(delayTime);
    return retryOnRecaptchaInvalid(attempt, retries, attemptNo + 1);
  }
};

/**
 * 429 on /auth/authenticate: the BE only says "Rate limit exceeded.", so show a translated message
 * (for ErrorAlert, which translates `message` when `translate` is set) with the wait time from X-Rate-Limit-Try-After-Seconds.
 */
export const toLoginRateLimitError = (err: any) => {
  const seconds = err?.response?.headers?.["x-rate-limit-try-after-seconds"];
  return {
    ...err,
    response: {
      ...err?.response,
      data: {
        ...err?.response?.data,
        translate: true,
        message: seconds ? "rate_limited_login" : "rate_limited_login_later",
        params: { seconds: seconds ?? "" }, // ErrorAlert only translates when params is set.
      },
    },
  };
};

export const isLoginRateLimited = (err: any) =>
  err?.response?.status === 429 &&
  `${err?.config?.url ?? ""}`.endsWith("/auth/authenticate");

export const checkGoogleReachable = async (
  url: string,
  options = {},
  timeout = 3000
) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      // check origin
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    // @ts-ignore
    if (error?.name === "AbortError") {
      throw new Error("Request timed out");
    }
    throw error;
  } finally {
    clearTimeout(id);
  }
};

export const detectCaptchaFromScript = async (
  timeout = 3000
): Promise<CaptchaProvider> => {
  try {
    // testing on QA with "turnstile"
    if (localStorage.getItem("enabled_turnstile") === "true")
      return "turnstile";
    // Test Google reachability via either CDN or reCAPTCHA-specific domain.
    // One success is enough — Singapore sometimes blocks one but not the other.
    await Promise.any([
      checkGoogleReachable(
        "https://www.gstatic.com/generate_204",
        { method: "HEAD", mode: "no-cors" },
        timeout
      ),
      // checkGoogleReachable(
      //   "https://www.recaptcha.net/generate_204",
      //   { method: "HEAD", mode: "no-cors" },
      //   timeout
      // ),
    ]);
    // If Google reachable, try reCAPTCHA
    return "grecaptcha";
  } catch {
    // eslint-disable-next-line no-console
    console.log("Google un-reachability, we will use Turnstile for captcha.");
    // If unreachable, fallback to Turnstile
    return "turnstile";
  }
};

export const exposeCaptchaHeaders = (
  turnstile: boolean,
  recaptchaToken: string
) => {
  return turnstile
    ? { "x-turnstile": recaptchaToken }
    : { "x-recaptcha": recaptchaToken };
};
